import { useFormContext } from "react-hook-form";
import { DeliveryFormData } from "@/types/forms/home/cotationDeliveryForm.form";
import { GetAllCompanyDto } from "@/types/api/api-types";
import { useState } from "react";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import StyledInput from "@/components/styled/StyledInput";
import { AutocompleteSearch } from "@/components/pendingShipments/autocompleteSearch";
import StyledPhoneInput from "@/components/styled/StyledPhoneInput";
import StyledSwitch from "@/components/ui/customs/StyledSwitch";
import { useEffect } from "react";
import clsx from "clsx";
import { ControlledCountryComboboxCountryCode } from "@/components/ui/customs/controlled-combobox-countryCode";
import { CountryCode } from "libphonenumber-js";
import { UpdateCustomQuotationFormData } from "@/components/backOffice/pendingCustomQuotation/customQuotationQuote.form";
import { Button } from "@/components/ui/button";
import { Pencil, PencilOff } from "lucide-react";
import { ControlledInputV3 } from "@/components/ui/customs/controlled-input-v3";
import { ConfirmationModalProps } from "@/types/confirmationModal";
import { parseGooglePlaceAddress } from "@/lib/google-address.util";
import { useTranslation } from "react-i18next";

type Props = {
  setConfirmationModalState: (state: ConfirmationModalProps | null) => void;
  company: GetAllCompanyDto;
};

interface InfoPickupProps {
  setConfirmationModalState: (state: ConfirmationModalProps | null) => void;
  company?: GetAllCompanyDto;
}

export const InfoValidationBo = ({ company, setConfirmationModalState }: Props) => {
  const { t } = useTranslation("boExpeditionForm");
  const [toggleEdit, setToggleEdit] = useState(false);

  const { billingEntities, pickUpLocations } = company;

  const methods = useFormContext<DeliveryFormData>();

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-blue-50 p-4">
      <div className="flex w-full flex-row items-center justify-between gap-2">
        <div className="flex flex-row items-center gap-2">
          <div className="bg-brand600 rounded-full px-3 py-1 text-xl font-bold text-white">
            <p className="text-xl font-bold text-white">4</p>
          </div>
          <p className="text-xl font-bold">{t("infoValidation.title")}</p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setConfirmationModalState({
              title: toggleEdit
                ? t("infoValidation.lockEditTitle")
                : t("infoValidation.unlockEditTitle"),
              text: toggleEdit
                ? t("infoValidation.lockEditText")
                : t("infoValidation.unlockEditText"),
              confirm: () => {
                setToggleEdit(!toggleEdit);
              },
            });
          }}
          type="button"
        >
          {toggleEdit ? <PencilOff /> : <Pencil />}
        </Button>
      </div>
      <div className="flex w-full flex-col gap-1">
        <ControlledSelectV2
          options={billingEntities.map((billingEntity) => ({
            value: billingEntity.id,
            label: `${billingEntity.companyName}`,
          }))}
          setValue={(e: any) => methods.setValue("billingEntityId", e)}
          label={t("infoValidation.billingEntityLabel")}
          control={methods.control}
          name="billingEntityId"
          contentClassName="w-full"
          isDisabled={!toggleEdit}
        />
        <ControlledSelectV2
          control={methods.control}
          name="pickUpLocationId"
          options={pickUpLocations.map((pickUpLocation) => ({
            value: pickUpLocation.id,
            label: `${pickUpLocation.name} ${pickUpLocation.street} ${pickUpLocation.postalCode} ${pickUpLocation.city}`,
          }))}
          label={t("infoValidation.pickupLocationLabel")}
          contentClassName="w-full"
          isDisabled={!toggleEdit}
          setValue={(e: any) => methods.setValue("pickUpLocationId", e)}
        />
        <ControlledInputV3
          control={methods.control}
          name="reference"
          label={t("infoValidation.referenceLabel")}
          placeholder={t("infoValidation.referencePlaceholder")}
          className="w-full"
          disabled={!toggleEdit}
        />
      </div>
    </div>
  );
};

export const InfoValidationCustomQuotationBo = ({ company, setConfirmationModalState }: Props) => {
  const { t } = useTranslation("boExpeditionForm");
  const [toggleEdit, setToggleEdit] = useState(false);

  const { billingEntities } = company;

  const methods = useFormContext<UpdateCustomQuotationFormData>();

  const billingEntity = methods.watch("billingEntityId");

  const handleBillingEntityChange = (e: any) => {
    if (e === billingEntity) return;
    methods.setValue("billingEntityId", e);
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-blue-50 p-4">
      <div className="flex w-full flex-row items-center justify-between gap-2">
        <div className="flex flex-row items-center gap-2">
          <div className="bg-brand600 rounded-full px-3 py-1 text-xl font-bold text-white">
            <p className="text-xl font-bold text-white">3</p>
          </div>
          <p className="text-xl font-bold">{t("infoValidation.title")}</p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setConfirmationModalState({
              title: toggleEdit
                ? t("infoValidation.lockEditTitle")
                : t("infoValidation.unlockEditTitle"),
              text: toggleEdit
                ? t("infoValidation.lockEditText")
                : t("infoValidation.unlockEditText"),
              confirm: () => {
                setToggleEdit(!toggleEdit);
              },
            });
          }}
          type="button"
        >
          {toggleEdit ? <PencilOff /> : <Pencil />}
        </Button>
      </div>
      <div className="flex w-full flex-col gap-1">
        <ControlledSelectV2
          options={billingEntities.map((billingEntity) => ({
            value: billingEntity.id,
            label: `${billingEntity.companyName}`,
          }))}
          setValue={(e: any) => handleBillingEntityChange(e)}
          label={t("infoValidation.billingEntityLabel")}
          control={methods.control}
          isDisabled={!toggleEdit}
          name="billingEntityId"
          contentClassName="w-full"
        />
        <StyledInput
          control={methods.control}
          disabled={!toggleEdit}
          name="reference"
          title={t("infoValidation.referenceLabel")}
          className="w-full"
        />
      </div>
    </div>
  );
};

export const InfoPickupCustomQuotationBo = ({
  company,
  setConfirmationModalState,
}: InfoPickupProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<UpdateCustomQuotationFormData>();
  const [toggleEdit, setToggleEdit] = useState(false);

  const manualPickup = methods.watch("pickupInfo.manualPickup");
  const pickupCountry = methods.watch("pickupInfo.pickupCountry");

  const defaultCountry: CountryCode = (pickupCountry || "FR") as CountryCode;

  useEffect(() => {
    if (manualPickup) {
      methods.setValue("pickupInfo.pickupLocationId", "");
    } else {
      methods.setValue("pickupInfo.pickupCountry", "");
      methods.setValue("pickupInfo.pickupStreet", "");
      methods.setValue("pickupInfo.pickupPostalCode", "");
      methods.setValue("pickupInfo.pickupCity", "");
      methods.setValue("pickupInfo.pickupPhone", "");
      methods.setValue("pickupInfo.pickupCompany", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualPickup]);

  const handlePlaceClicked = (place: google.maps.places.PlaceResult | null) => {
    const parsed = parseGooglePlaceAddress(place, pickupCountry);
    if (!parsed) return;
    methods.setValue("pickupInfo.pickupStreet", parsed.street);
    methods.setValue("pickupInfo.pickupPostalCode", parsed.postalCode);
    methods.setValue("pickupInfo.pickupCity", parsed.city);
    methods.trigger([
      "pickupInfo.pickupStreet",
      "pickupInfo.pickupPostalCode",
      "pickupInfo.pickupCity",
      "pickupInfo.pickupCountry",
    ]);
  };

  const handleStreetInput = (e: string) => {
    methods.setValue("pickupInfo.pickupStreet", e);
    methods.trigger(["pickupInfo.pickupStreet"]);
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-blue-50 p-4">
      <div className="flex w-full flex-row items-center justify-between gap-2">
        <div className="flex flex-row items-center gap-2">
          <div className="bg-brand600 rounded-full px-3 py-1 text-xl font-bold text-white">
            <p className="text-xl font-bold text-white">2</p>
          </div>
          <p className="text-xl font-bold">{t("infoPickup.title")}</p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setConfirmationModalState({
              title: toggleEdit ? t("infoPickup.lockEditTitle") : t("infoPickup.unlockEditTitle"),
              text: toggleEdit ? t("infoPickup.lockEditText") : t("infoPickup.unlockEditText"),
              confirm: () => {
                setToggleEdit(!toggleEdit);
              },
            });
          }}
          type="button"
        >
          {toggleEdit ? <PencilOff /> : <Pencil />}
        </Button>
      </div>
      <div className="flex w-full flex-col gap-2">
        <ControlledSelectV2
          control={methods.control}
          name="pickupInfo.pickupLocationId"
          options={
            company?.pickUpLocations.map((pickUpLocation) => ({
              value: pickUpLocation.id,
              label: `${pickUpLocation.name} ${pickUpLocation.street} ${pickUpLocation.postalCode} ${pickUpLocation.city}`,
            })) || []
          }
          placeholder={t("infoPickup.pickupLocationPlaceholder")}
          isDisabled={manualPickup || !toggleEdit}
        />
      </div>
      <div className="flex w-full flex-row items-center gap-4">
        <StyledSwitch
          control={methods.control}
          name="pickupInfo.manualPickup"
          disabled={!toggleEdit}
        />
        <span className="text-gray-800">{t("infoPickup.otherPickupLocation")}</span>
      </div>
      <div
        className={clsx("flex w-full flex-col gap-2", {
          hidden: !manualPickup,
        })}
      >
        <div className="flex w-full flex-col gap-2 sm:flex-row">
          <StyledInput
            control={methods.control}
            name="pickupInfo.pickupLastName"
            title={t("infoPickup.contactLastNameLabel")}
            className="w-full"
            isReadOnly={!toggleEdit}
          />
          <StyledInput
            control={methods.control}
            name="pickupInfo.pickupFirstName"
            title={t("infoPickup.contactFirstNameLabel")}
            className="w-full"
            isReadOnly={!toggleEdit}
          />
        </div>
        <StyledInput
          control={methods.control}
          name="pickupInfo.pickupCompany"
          title={t("infoPickup.companyLabel")}
          isReadOnly={!toggleEdit}
        />
        <ControlledCountryComboboxCountryCode
          name="pickupInfo.pickupCountry"
          control={methods.control}
          placeholder={t("infoPickup.countryPlaceholder")}
          wrapperClassName="pb-2"
          isDisabled={!toggleEdit}
        />
        <AutocompleteSearch
          key={pickupCountry}
          placeholder={t("infoPickup.streetPlaceholder")}
          onPlaceClicked={handlePlaceClicked}
          control={methods.control}
          handleStreetInput={handleStreetInput}
          formValueOverride="pickupInfo.pickupStreet"
          disabled={!toggleEdit || !pickupCountry}
          countryCode={pickupCountry ? [pickupCountry] : []}
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <StyledInput
            className="w-full sm:w-[40%] md:w-[25%]"
            control={methods.control}
            name="pickupInfo.pickupPostalCode"
            title={t("infoPickup.postalCodeLabel")}
            isReadOnly={!toggleEdit || !pickupCountry}
          />
          <StyledInput
            className="flex-1"
            control={methods.control}
            name="pickupInfo.pickupCity"
            title={t("infoPickup.cityLabel")}
            isReadOnly={!toggleEdit || !pickupCountry}
          />
        </div>
        <StyledPhoneInput
          key={defaultCountry}
          control={methods.control}
          name="pickupInfo.pickupPhone"
          title={t("infoPickup.phoneLabel")}
          disabled={!toggleEdit || !pickupCountry}
          defaultCountry={defaultCountry}
        />
      </div>
    </div>
  );
};
